/* eslint-disable max-lines */
import {
    FunnelViewType,
    LoginFormInputs,
    SsoProps,
} from './types'
import {
    NinchatErrorEvent,
    NinchatSsoBegunEvent,
} from 'api/types/events'

import React, {
    useContext,
    useState,
} from 'react'

import {
    joinChannel, startJoinChannelListening,
} from 'api/action/joinChannel'

import {
    useAppDispatch,
    useAppSelector,
} from 'hooks'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import {EmbedLogin} from 'components/Embed/EmbedLogin'

import {IdentityType} from 'api/types/properties'
import Loader from '../Loader'
import LoginView from './LoginView'
import LoginViewSsoError from './LoginViewSsoError'
import {NavigationComponentProps} from 'navigation/types'
import {NinchatErrorType} from 'api/types/errors'
import {beginSso} from 'api/action/beginSso'
import {closeSession} from 'api/action/closeSession'
import {createSession} from 'api/action/createSession'
import {navigation} from '../../navigation'
import router from 'navigation/router'
import {updateLoginErrorType} from 'state/ui'
import {updateMember} from 'api/action/updateMember'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const hasLoginError = (funnelView: FunnelViewType, errorMessage?: string) : boolean => (errorMessage?.length ?? 0) > 0 && funnelView === 'email' // only show error in first funnel view
const nextFunnelView = (currentView: FunnelViewType) : FunnelViewType => {
    if (currentView === 'email') {
        return 'password'
    } else if (currentView === 'password') {
        return 'completed'
    }
    return 'completed'
}

const Login: React.FC<NavigationComponentProps<'Login'> & { resetEmbed?: () => void }> = (props): React.ReactElement => {
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'
    const isEmbedSso = Boolean(embed?.sso?.name || embed?.sso?.type === 'email')
    const dispatch = useAppDispatch()
    const {t} = useTranslation()
    const sendToParent = useSendToParent()

    const {resetEmbed} = props
    const {
        passwordUpdated,
        ssoError,
        ssoErrorType,
    } = navigation.params(props as NavigationComponentProps<'Login'>) || {}

    const [initialRoute] = useAppSelector(({navigation: n}) => n.routes, () => true)

    const loginError = useAppSelector(({ui}) => ui.login_error?.error_type)
    const loginErrorTime = useAppSelector(({ui}) => ui.login_error?.at)
    const previousLoginErrorTime = React.useRef(loginErrorTime).current

    const [connecting, setConnecting] = useState<boolean | string>(false)
    const [ssoChecked, setSsoChecked] = useState<boolean | string>(false)

    const userId = useAppSelector(({ninchat}) => ninchat.user_id)
    const userAuth = useAppSelector(({ninchat}) => ninchat.user_auth)

    const redirectTo = (ssoURL: string) => {
        if (embed?.newSso) {
            sendToParent({
                event: EmbedEvent.EmbedSSO,
                targetOrigin: embed.targetOrigin || '',
                url: ssoURL,
            })
        } else {
            window.location.replace(ssoURL)
        }
    }

    const connectClient = () => {
        setConnecting('Connecting')
        dispatch(createSession())
            .unwrap()
            .then(() => {
                dispatch(updateLoginErrorType(undefined))
            })
            .catch((event) => {
                if (event.error_type) {
                    dispatch(updateLoginErrorType(event.error_type))
                }
            })
            .finally(() => setConnecting(false))
    }
    const disconnectClient = () => dispatch(closeSession())
    const onLogin = ({
        emailAddress, password, rememberLogin, otp,
    }: {emailAddress: string, password: string, rememberLogin?: boolean, otp?: number}) => {
        setConnecting(t('Signing in using {{email}}', {email: emailAddress}))
        dispatch(createSession({
            identity_auth: password,
            identity_name: emailAddress,
            identity_type: 'email',
            persist: rememberLogin,
            user_otp: otp,
        }))
            .unwrap()
            .then(() => {
                dispatch(updateLoginErrorType(undefined))
            })
            .catch((event) => {
                if (event.error_type) {
                    dispatch(updateLoginErrorType(event.error_type))
                }
            })
            .finally(() => setConnecting(false))
    }

    const [formData, setFormData] = useState<LoginFormInputs>({} as LoginFormInputs)
    const [funnelView, setFunnelView] = useState<FunnelViewType>('email')

    const handleLogin = (data: LoginFormInputs): void => {
        onLogin({
            emailAddress: data.email,
            otp: data.otp ? Number(data.otp) : undefined,
            password: data.password,
            rememberLogin: data.rememberMe,
        })
    }
    const handleEmbedLogin = (): void => {
        const accessKey = embed?.scheduleAccessKey || embed?.ssoAccessKey

        dispatch(createSession({
            access_key: accessKey,
            audienceRealmId: embed?.audienceRealmId,
            persist: !!embed?.remember,
            ...embed?.scheduleAccessKey
                ? {}
                : {
                    user_auth: userAuth,
                    user_id: userId || undefined,
                },
        }))
            .unwrap()
            .then((user) => {
                if (embed?.scheduleAccessKey) {
                    const channelKeys = Object.keys(user.user_channels || {})

                    dispatch(updateMember(
                        {
                            channel_id: channelKeys[0],
                            member_attrs: {invitee: false},
                            user_id: user.user_id,
                        },
                    )).unwrap()
                        .catch(() => {
                            embed?.setEmbedState((prevState) => ({
                                ...prevState,
                                error: {
                                    message: t('We encountered an error. Please close and restart chat to continue.'),
                                    title: t('Error joining schedule meeting'),
                                },
                            }))
                        })
                } else if (embed?.channelId) {
                    dispatch(joinChannel({channel_id: embed.channelId})).unwrap()
                        .catch(() => {
                            embed?.setEmbedState((prevState) => ({
                                ...prevState,
                                error: {
                                    message: t('We encountered an error. Please close and restart chat to continue.'),
                                    title: t('Error joining channel'),
                                },
                            }))
                        })
                }
            })
            .catch((event) => {
                dispatch(updateLoginErrorType(event.error_type))

                if (embed?.scheduleAccessKey && event.error_type === NinchatErrorType.NinchatAccessExpiredError) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        error: {
                            message: t('We encountered an error. Please close and restart chat to continue.'),
                            title: t('Error joining schedule meeting. Access key has expired.'),
                        },
                    }))
                } else if (embed?.scheduleAccessKey) {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        error: {
                            message: t('We encountered an error. Please close and restart chat to continue.'),
                            title: t('Error joining schedule meeting'),
                        },
                    }))
                } else {
                    embed?.setEmbedState((prevState) => ({
                        ...prevState,
                        error: {
                            message: t('We encountered an error. Please close and restart chat to continue.') + (event.error_type ? ` (${event.error_type})` : ''),
                            title: t('Error creating session'),
                        },
                    }))
                }
            })
            .finally(() => setConnecting(false))
    }

    const onForgotPassword = (): void => {
        navigation.push('ForgotPassword')
    }
    const checkSso = (ssoProps: SsoProps): void => {
        setConnecting(true)

        const initialUrl = initialRoute && router.stringify(initialRoute.name, initialRoute.params)
        let [url] = location.href.split('/#')
        let access_url = initialUrl ? `${url}/#${initialUrl}` : url

        if (embed?.newSso) {
            url = embed?.parentUrl || ''
            access_url = `${url}`
        }

        const {
            email,
            name,
        } = ssoProps

        const action = {
            access_url,
            ...!email && name && {sso_name: name},
            ...!name && email && {
                identity_name: email,
                identity_type: 'email' as IdentityType,
            },
        }

        dispatch(beginSso(action))
            .unwrap()
            .then(
                (event: NinchatSsoBegunEvent) => {
                    if (event.sso_url) {
                        redirectTo(event.sso_url)
                    } else {
                        setFunnelView(nextFunnelView(funnelView))
                        setConnecting(false)
                        setSsoChecked(true)
                    }
                },
            )
            .catch(
                (error: NinchatErrorEvent) => {
                    const {error_type} = error
                    if (isEmbedSso) {
                        // if we are forcing sso from embed always redirect to login with sso error
                        navigation.replace('Login', {
                            ssoError: true,
                            ssoErrorType: error_type,
                        })
                        return
                    } else if (error_type === NinchatErrorType.NinchatSsoNotAvailable) {
                        // only redirect to next funnel view if error type is NinchatSsoNotAvailable
                        setFunnelView(nextFunnelView(funnelView))
                    } else {
                        dispatch(updateLoginErrorType(error_type))
                    }
                    setConnecting(false)
                    setSsoChecked(true)
                },
            )
    }
    const onSubmitFormData = (data: Partial<LoginFormInputs>) => {
        const nextFormData = {
            ...formData,
            ...data,
        }
        if (nextFunnelView(funnelView) === 'completed') {
            handleLogin(nextFormData)
            return
        }
        if (data.email) {
            checkSso({email: data.email})
        }
        setFormData(nextFormData)
    }

    React.useEffect(() => {
        startJoinChannelListening(isEmbedQueueChannel)
    }, [])

    React.useEffect(() => {
        // A failed SSO sign-in routes here with ssoError set; skip auto SSO/session
        // actions. Retry clears ssoError so this effect re-runs and restarts SSO.
        if (ssoError) {
            setSsoChecked(true)
            return
        }

        // for queue and channel
        if (isEmbedQueueChannel) {
            handleEmbedLogin()
            return
        }

        if (embed?.sso?.name) {
            checkSso({name: embed.sso.name})
        } else {
            switch (embed?.sso?.type) {
                case 'email':
                    if (embed?.sso?.email) {
                        checkSso({email: embed.sso.email})
                    }
                    break
                default:
                    connectClient()
            }
        }
    }, [ssoError])

    React.useEffect(() => {
        if (loginError && loginErrorTime !== previousLoginErrorTime) {
            disconnectClient()
        }
    }, [loginError, loginErrorTime])

    const isEmbedSsoNotReady = (embed?.sso?.name || embed?.sso?.type === 'email') && !ssoChecked


    if (ssoError) {
        return <LoginViewSsoError ssoErrorType={ssoErrorType} />
    }

    return (
        connecting || isEmbedSsoNotReady ?
            <Loader label={typeof connecting === 'string' ? connecting : undefined} /> :
            isEmbedQueueChannel ?
                <EmbedLogin resetEmbed={() => resetEmbed && resetEmbed()} /> :
                <LoginView
                    email={formData.email}
                    error={loginError}
                    funnelView={funnelView}
                    hasLoginError={hasLoginError}
                    isConnecting={!!connecting}
                    onForgotPassword={onForgotPassword}
                    onSubmitFormData={onSubmitFormData}
                    otp={formData.otp}
                    password={formData.password}
                    passwordUpdated={passwordUpdated}
                    setFunnelView={setFunnelView}
                />
    )
}
export default Login

Login.defaultProps = {resetEmbed: undefined}
